<template>
  <div>
    <b-modal @hide="hideModal()" :visible="modal.show" @show="onModalShow" size="md">
      <template v-slot:modal-header>
        <h5 class="modal-title">
          {{ $t('Pause') }} {{ sale.title | masterLanguage }}
        </h5>
        <button type="button" @click="hideModal()" aria-label="Close" class="close">×
        </button>
      </template>
      <template v-slot:modal-footer>
        <cancel-button :disabled="loading" @click="hideModal()"/>
        <submit-button variant="success" :disabled="loading " :show-loader="loading"
                       @click="onSubmitClick"/>
      </template>
      <div class="mt-2" v-if="!loading">
        <div class="with-tab" v-if="tabLayout">
          <b-tabs v-model="activeLanguage" class="custom-tabs" content-class="bg-white pt-3 pb-3" v-if="tabLayout">
            <b-tab v-for="(locale) in availableLocales" :key="`language_tab_${locale.key}`"
                   :title-link-class="hasErrors(locale.key) ? 'has-error' : ''">
              <template v-slot:title>
                {{ locale.name }}
                <span v-if="hasErrors(locale.key)" style="width: 20px;height: 20px" id="exclamation-icon">
                        <font-awesome-icon class="text-danger hover-cursor" icon="exclamation-circle"/>
                      </span>
                <b-tooltip target="exclamation-icon"
                           :title="$t('You have validation errors')"/>
              </template>
              <div class="row">
                <div class="col-md-12">
                  <label for="">{{ $t('Pause Message:') }}</label>
                  <ckeditor :editor="ckeditorConfig.editor" ref="currentInput" tag-name="div"
                            :name="`pause-message-${locale.key}`" :key="`pause-message-${locale.key}`"
                            :class="{'is-invalid': hasErrors(locale.key)}" v-model="pause_message[locale.key]"
                            :config="ckeditorConfig.editorConfig"/>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>
        <div v-else class="col-md-12">
          <label for="">{{ $t('Pause Message:') }}</label>
          <ckeditor :editor="ckeditorConfig.editor" ref="currentInput" tag-name="div"
                    :name="`pause-message-${masterLanguage.key}`" :key="`pause-message-${masterLanguage.key}`"
                    :class="{'is-invalid': hasErrors(masterLanguage.key)}" v-model="pause_message[masterLanguage.key]"
                    :config="ckeditorConfig.editorConfig"/>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import CancelButton from "../../core/components/common/CancelButton";
  import SubmitButton from "../../core/components/common/SubmitButton";
  import {createNamespacedHelpers} from "vuex";
  import i18nHelper from "@/core/services/i18n.service";
  import InlineEditor from '@ckeditor/ckeditor5-build-inline';

  const {mapState, mapActions} = createNamespacedHelpers('sales');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global')

  export default {
    name: "SalePauseModal",
    components: {SubmitButton, CancelButton},
    computed: {
      ...mapStateGlobal(['locales']),
      ...mapState({
        modal: state => state.salePauseModal,
        sale: state => state.view.currentSale
      }),
      ckeditorConfig() {
        return getCkeditorDefaultConfig();
      },
      availableLocales() {
        let masterLang = i18nHelper.getMasterLanguage();
        let localeKeys = Object.keys(this.locales.available_locales)

        // Move master lang to beginning of array
        if (localeKeys.indexOf(masterLang.key)) {
          localeKeys.splice(localeKeys.indexOf(masterLang.key), 1);
          localeKeys.unshift(masterLang.key);
        }

        return localeKeys.map(lang => {
          return {
            key: lang,
            name: this.locales.available_locales[lang]
          }
        })
      },
      tabLayout() {
        return this.availableLocales.length > 1
      },
    },
    data() {
      return {
        loading: true,
        activeLanguage: null,
        masterLanguage: i18nHelper.getMasterLanguage(),
        pause_message: {},
      }
    },
    methods: {
      ...mapActions(['hideSalePauseModal', 'pauseResumeAction']),
      hasErrors(key) {
        return !this.pause_message[key];
      },
      hideModal() {
        this.loading = true;
        this.pause_message = {};
        this.activeLanguage = null;
        this.hideSalePauseModal();
      },
      onModalShow() {
        this.availableLocales.forEach(l => {
          this.pause_message[l.key] = this.sale.pause_message && this.sale.pause_message[l.key] ? this.sale.pause_message[l.key] :
            this.$i18n.t("We are currently facing technical difficulties and auction has been paused, please check back in a short while. Don't worry, bidding timer will not run out on the lots while the auction is paused and you will be notified when we are back.");
        });
        this.loading = false;
      },
      async onSubmitClick(_, extraHandler) {
        this.loading = true;

        let success = await this.$confirm(
          this.$i18n.t(`Pausing an auction will freeze the auction and make it inaccessible by your bidders. Would you like to proceed?`),
          null,
          null,
          null,
          this.$i18n.t('Please Confirm'),
          this.$i18n.t('Yes'),
          this.$i18n.t('No'),
          null,
          {
            label: this.$i18n.t("To proceed please type 'pause' below and click Submit"),
            text: 'pause'
          }
        )
        if (!success) {
          this.hideModal();
          return;
        }

        let response = await this.pauseResumeAction({
          uuid: this.sale.uuid,
          is_paused: true,
          resume_date: null,
          pause_message: this.pause_message
        });
        this.loading = true;
        if (response.success) {
          this.$toast('success', this.$i18n.t('Auction successfully paused.'));
          this.hideModal();
        } else {
          if (response.body && response.body.non_field_errors) {
            if (typeof response.body.non_field_errors === 'object' && response.body.non_field_errors.length > 1) {
              const listItemsArr = [];
              response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
            } else {
              this.$errorDialog(response.body.non_field_errors[0]);
            }
          }
          this.hideModal();
        }
      }
    }
  }

  function getCkeditorDefaultConfig() {
    return {
      editor: InlineEditor,
      editorData: '',
      editorConfig: {
        toolbar: ['bold', 'italic', 'heading', 'link', 'bulletedList', 'numberedList', 'indent', 'outdent', 'blockQuote']
      }
    };
  }
</script>

<style scoped>

</style>
